import { Calendar, Cloud, ExternalLink } from "lucide-react";
import { formatDate } from "@/lib/utils";
import type { AdminDealDrawerPayload } from "./use-admin-deal";

// SPA port of DealSummaryPanel — read-only HubSpot deal snapshot.

export function DealSummaryPanel({
  deal,
}: {
  deal: AdminDealDrawerPayload["deal"];
}) {
  return (
    <section aria-label="Deal" className="rounded-lg border bg-card px-4 py-3">
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Deal
          </p>
          <h3 className="mt-0.5 truncate font-display text-base tracking-tight text-foreground">
            {deal.deal_name || "(unnamed deal)"}
          </h3>
        </div>
        <a
          href={deal.hubspot_url}
          target="_blank"
          rel="noreferrer noopener"
          className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-background px-2.5 py-1 text-[11px] font-medium text-foreground hover:bg-muted"
        >
          <ExternalLink className="h-3 w-3" />
          View in CRM
        </a>
      </header>

      <dl className="grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
        {deal.deal_stage_label && (
          <Field label="Stage">
            <span className="inline-flex items-center gap-1">
              <Cloud className="h-3 w-3 text-accent" />
              {deal.deal_stage_label}
            </span>
          </Field>
        )}
        {deal.deal_amount != null && (
          <Field label="Amount">
            {formatAmount(deal.deal_amount, deal.deal_currency_code ?? "EUR")}
          </Field>
        )}
        {deal.deal_close_date && (
          <Field label="Close date">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              {formatDate(deal.deal_close_date)}
            </span>
          </Field>
        )}
        {deal.deal_created_at && (
          <Field label="Created">
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              {formatDate(deal.deal_created_at)}
            </span>
          </Field>
        )}
      </dl>

      {deal.pulled_at && (
        <p className="mt-3 border-t pt-2 text-[10px] text-muted-foreground">
          Snapshot from HubSpot · pulled {formatDate(deal.pulled_at)}
        </p>
      )}
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 truncate text-foreground">{children}</dd>
    </div>
  );
}

function formatAmount(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString("en-IE", { maximumFractionDigits: 0 })} ${currency}`;
  }
}
